import { useState } from "react";
import { ChevronDown, ChevronRight, Calculator, Ship, Anchor, Globe, Container, MapPin, Percent, Thermometer } from "lucide-react";

const EUMRVCalculations = () => {
  const [openSections, setOpenSections] = useState(false);

  const toggleSection = () => {
    setOpenSections((prev) => !prev);
  };

  const coreCalculations = [
    {
      id: "fuel-consumption-aggregation",
      title: "Fuel Consumption Aggregation",
      description: "Sums fuel consumption from all vessel systems for each fuel type",
      formula: "Total Fuel = Main Engine + Aux Engine + Boiler + Incinerator + IGG",
      subFormulas: [
        "Main_Engine_Cons_MT_${fuelType}",
        "Aux_Engine_Cons_MT_${fuelType}",
        "Boiler_Cons_MT_${fuelType}",
        "Incinerator_Cons_MT_${fuelType}",
        "IGG_Cons_MT_${fuelType}",
        "Vessel fuel types: HFO, LFO, MDO, MGO, LNG"
      ],
      icon: <Calculator className="w-5 h-5" />,
      borderColor: "border-blue-200",
      bgColor: "bg-blue-50",
      textColor: "text-blue-700",
    },
    {
      id: "emission-calculation",
      title: "Emission Calculation",
      description: "Calculates CO₂, CH₄, N₂O emissions using database emission factors",
      formula: "Emissions = Fuel × Emission Factor (with CSlip for CH₄)",
      subFormulas: [
        "CO₂ = Fuel × CF_CO₂",
        "CH₄ = Fuel × CF_CH₄ + (Fuel × CSlip%)",
        "N₂O = Fuel × CF_N₂O",
        "Factors from FuelTable: CF_C02, CF_CH4, CF_N20, CSlip",
        "CSlip applied only to CH₄ emissions"
      ],
      icon: <Thermometer className="w-5 h-5" />,
      borderColor: "border-green-200",
      bgColor: "bg-green-50",
      textColor: "text-green-700",
    },
    {
      id: "ghg-calculation",
      title: "Total GHG Calculation",
      description: "Calculates total greenhouse gas emissions using Global Warming Potentials",
      formula: "GHG (t CO₂e) = CO₂ + (CH₄ × 28) + (N₂O × 265)",
      subFormulas: [
        "GWP_CO₂ = 1",
        "GWP_CH₄ = 28 (28× CO₂ equivalent)",
        "GWP_N₂O = 265 (265× CO₂ equivalent)",
        "Units: Metric Tons CO₂ equivalent",
        "Separate calculations for ETS-covered emissions"
      ],
      icon: <Thermometer className="w-5 h-5" />,
      borderColor: "border-amber-200",
      bgColor: "bg-amber-50",
      textColor: "text-amber-700",
    },
    {
      id: "ets-coverage-percentage",
      title: "ETS Coverage Percentage",
      description: "Determines percentage of emissions covered by EU ETS based on voyage type",
      formula: "MRV Fraction = getEUMRVFraction(segmentType)",
      subFormulas: [
        "100% coverage: at_eu_port, between_eu_ports, betweenTranshipmentPorts",
        "50% coverage: from_eu_port, to_eu_port, transhipment segments",
        "0% coverage: outside_eu_scope, eca_zone",
        "Applied to departed/arrived segments"
      ],
      icon: <Percent className="w-5 h-5" />,
      borderColor: "border-purple-200",
      bgColor: "bg-purple-50",
      textColor: "text-purple-700",
    }
  ];

  const segmentClassification = [
    {
      id: "port-classification",
      title: "Port Classification",
      description: "Identifies EU/UK ports using PortCompliance collection",
      formula: "isEUOrUKPort = PortCompliance.eea === true OR PortCompliance.uk === true",
      subFormulas: [
        "Database lookup: portName or portCode match",
        "Case-insensitive matching",
        "Includes both EU and UK ports",
        "Returns boolean result"
      ],
      icon: <Anchor className="w-5 h-5" />,
      borderColor: "border-indigo-200",
      bgColor: "bg-indigo-50",
      textColor: "text-indigo-700",
    },
    {
      id: "transhipment-port-logic",
      title: "Transhipment Port Logic",
      description: "Special handling for container vessel transhipment ports",
      formula: "TRANSHIPMENT_PORTS = {'east port said', 'tanger med'}",
      subFormulas: [
        "Only for container vessels (vesselType includes 'container')",
        "Normalization: lowercase, remove special chars, trim spaces",
        "Additional segments for transhipment operations",
        "Affects ETS coverage percentages"
      ],
      icon: <Container className="w-5 h-5" />,
      borderColor: "border-orange-200",
      bgColor: "bg-orange-50",
      textColor: "text-orange-700",
    },
    {
      id: "segment-determination",
      title: "Segment Determination Logic",
      description: "Classifies voyages into 11 distinct EU MRV segments",
      formula: "segmentType = determineEUSegmentTypeFromRaw(record, vessel)",
      subFormulas: [
        "at_eu_port: Current port is EU, no voyage",
        "between_eu_ports: Both ports are EU",
        "from_eu_port: EU → Non-EU",
        "to_eu_port: Non-EU → EU",
        "Transhipment segments for container vessels",
        "outside_eu_scope: No EU ports involved"
      ],
      icon: <MapPin className="w-5 h-5" />,
      borderColor: "border-teal-200",
      bgColor: "bg-teal-50",
      textColor: "text-teal-700",
    },
    {
      id: "container-vessel-logic",
      title: "Container Vessel Special Logic",
      description: "Enhanced segmentation for container ships with transhipment",
      formula: "isContainerVessel = vesselType.toLowerCase().includes('container')",
      subFormulas: [
        "nonEEAToTranshipment: Non-EU → Transhipment port",
        "transhipmentToEEA: Transhipment port → EU",
        "EEAToTranshipment: EU → Transhipment port",
        "transhipmentToNonEEA: Transhipment port → Non-EU",
        "betweenTranshipmentPorts: Transhipment → Transhipment",
        "50% ETS coverage for all transhipment segments"
      ],
      icon: <Ship className="w-5 h-5" />,
      borderColor: "border-pink-200",
      bgColor: "bg-pink-50",
      textColor: "text-pink-700",
    }
  ];

  const complianceCalculations = [
    {
      id: "reduction-factor",
      title: "Reduction Factor",
      description: "Annual phase-in factor for ETS compliance",
      formula: "Reduction Factor = calculateReductionFactor(benchmarkYear)",
      subFormulas: [
        "2024: 0.4 (40% of emissions covered)",
        "2025: 0.7 (70% of emissions covered)",
        "Default: 1.0 (100% if year not found)",
        "Applied to ETS-covered emissions only"
      ],
      icon: <Percent className="w-5 h-5" />,
      borderColor: "border-rose-200",
      bgColor: "bg-rose-50",
      textColor: "text-rose-700",
    },
    {
      id: "ice-class-discount",
      title: "Ice Class Discount",
      description: "5% discount for qualifying ice-class vessels until 2030",
      formula: "Ice Discount = qualifiesForIceDiscount ? 0.95 : 1.0",
      subFormulas: [
        "Requirements: iceClassShip = 'YES' AND iceClassType includes 'IA'",
        "Valid until: benchmarkYear ≤ 2030",
        "Discount: 5% (0.95 multiplier)",
        "Applied to final EUAs calculation"
      ],
      icon: <Ship className="w-5 h-5" />,
      borderColor: "border-cyan-200",
      bgColor: "bg-cyan-50",
      textColor: "text-cyan-700",
    },
    {
      id: "eua-calculation",
      title: "EU Allowances (EUA) Calculation",
      description: "Final EU Allowances required for compliance",
      formula: "EUAs = Reduction Factor × GHG_ETS × Ice Discount",
      subFormulas: [
        "GHG_ETS = Sum of ETS-covered emissions",
        "Reduction Factor based on year",
        "Ice Discount if applicable",
        "Units: Metric Tons CO₂ equivalent"
      ],
      icon: <Calculator className="w-5 h-5" />,
      borderColor: "border-gray-200",
      bgColor: "bg-gray-50",
      textColor: "text-gray-700",
    },
    {
      id: "performance-metrics",
      title: "Performance Metrics",
      description: "Calculates operational performance indicators",
      formula: "Transport Work = Cargo Mass (MT) × Distance (NM)",
      subFormulas: [
        "Distance: From Distance Sailed (NM) field",
        "Cargo Mass: From Cargo Quantity (MT) field",
        "Sea Days: Based on vessel status",
        "Total Fuel: Sum of all fuel consumption"
      ],
      icon: <Calculator className="w-5 h-5" />,
      borderColor: "border-yellow-200",
      bgColor: "bg-yellow-50",
      textColor: "text-yellow-700",
    }
  ];

  const dataSources = [
    {
      collection: "VesselRawData",
      fields: [
        "Report Date Local Time",
        "From_Port", "To_Port", "Current_Port_Name",
        "Main_Engine_Cons_MT_*", "Aux_Engine_Cons_MT_*",
        "Boiler_Cons_MT_*", "Incinerator_Cons_MT_*", "IGG_Cons_MT_*",
        "Distance Sailed (NM)", "Cargo Quantity (MT)", "Status", "Zone"
      ],
      purpose: "Raw voyage and consumption data"
    },
    {
      collection: "Port",
      fields: ["portName", "portCode", "eea (boolean)"],
      purpose: "EEA Port Identification",
    },
    {
      collection: "FuelTable",
      fields: ["CF_C02", "CF_CH4", "CF_N20", "CSlip", "Fuel_Type"],
      purpose: "Emission factors"
    },
    {
      collection: "Vessel",
      fields: ["imoNumber", "vesselType", "fuelTypes", "iceClassShip", "iceClassType"],
      purpose: "Vessel configuration"
    }
  ];

  const segmentTypes = [
    { type: "at_eu_port", coverage: "100%", description: "At EU Port (no voyage)" },
    { type: "between_eu_ports", coverage: "100%", description: "Between EU Ports" },
    { type: "from_eu_port", coverage: "50%", description: "EU → Non-EU" },
    { type: "to_eu_port", coverage: "50%", description: "Non-EU → EU" },
    { type: "nonEEAToTranshipment", coverage: "50%", description: "Non-EU → Transhipment" },
    { type: "transhipmentToEEA", coverage: "50%", description: "Transhipment → EU" },
    { type: "EEAToTranshipment", coverage: "50%", description: "EU → Transhipment" },
    { type: "transhipmentToNonEEA", coverage: "50%", description: "Transhipment → Non-EU" },
    { type: "betweenTranshipmentPorts", coverage: "100%", description: "Between Transhipment Ports" },
    { type: "eca_zone", coverage: "0%", description: "In ECA Zone" },
    { type: "outside_eu_scope", coverage: "0%", description: "Outside EU Scope" }
  ];

  const transhipmentPorts = ["east port said", "tanger med"];

  return (
    <div>
      <div className="mb-6 border border-gray-300 rounded-lg overflow-hidden">
        <button
          className="flex items-center justify-between w-full p-5 bg-gray-50 hover:bg-gray-100 transition-colors"
          onClick={toggleSection}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[#e9cbf5] rounded-lg">
              <Calculator className="w-6 h-6 text-[#7d3c98]" />
            </div>
            <div className="text-left">
              <h2 className="text-md font-bold text-gray-800">
                EU MRV Emissions Calculations
              </h2>
              <p className="text-gray-600 text-sm">
                Comprehensive EU Monitoring, Reporting and Verification methodology
              </p>
            </div>
          </div>
          {openSections ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {openSections && (
          <div className="p-5 bg-white border-t border-gray-200">
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Data Sources</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 mb-6">
                {dataSources.map((source, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h4 className="font-semibold text-gray-800 mb-2">{source.collection}</h4>
                    <div className="text-sm text-gray-600 mb-2">{source.purpose}</div>
                    <div className="text-xs text-gray-500 space-y-1">
                      {source.fields.map((field, idx) => (
                        <div key={idx} className="font-mono truncate">{field}</div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Segment Types & Coverage</h3>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {segmentTypes.map((segment, index) => (
                    <div key={index} className="bg-white p-3 rounded border">
                      <div className="font-semibold text-gray-800 text-sm">{segment.type}</div>
                      <div className={`text-sm font-medium ${segment.coverage === '100%' ? 'text-green-600' : segment.coverage === '50%' ? 'text-amber-600' : 'text-red-600'}`}>
                        ETS Coverage: {segment.coverage}
                      </div>
                      <div className="text-xs text-gray-500">{segment.description}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Transhipment Configuration</h3>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-orange-800 mb-2">Transhipment Ports:</h4>
                    <ul className="text-sm text-orange-700 space-y-1">
                      {transhipmentPorts.map((port, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <span className="text-orange-400">•</span>
                          {port}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Core Calculations</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {coreCalculations.map((calc) => (
                  <div
                    key={calc.id}
                    className={`p-5 rounded-lg border ${calc.borderColor} ${calc.bgColor}`}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`p-2 rounded-lg ${calc.textColor} bg-white`}>
                        {calc.icon}
                      </div>
                      <div>
                        <h4 className={`font-bold text-gray-800 mb-1 ${calc.textColor}`}>
                          {calc.title}
                        </h4>
                        <p className="text-sm text-gray-600 mb-4">{calc.description}</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-1">Formula:</p>
                        <p className="text-sm font-semibold bg-white p-3 rounded border border-gray-200 font-mono">
                          {calc.formula}
                        </p>
                      </div>
                      
                      {calc.subFormulas && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Details:</p>
                          <div className="space-y-2">
                            {calc.subFormulas.map((subFormula, idx) => (
                              <div key={idx} className="text-sm bg-white p-2 rounded border border-gray-100 font-mono">
                                {subFormula}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Segment Classification</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {segmentClassification.map((calc) => (
                  <div
                    key={calc.id}
                    className={`p-5 rounded-lg border ${calc.borderColor} ${calc.bgColor}`}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`p-2 rounded-lg ${calc.textColor} bg-white`}>
                        {calc.icon}
                      </div>
                      <div>
                        <h4 className={`font-bold text-gray-800 mb-1 ${calc.textColor}`}>
                          {calc.title}
                        </h4>
                        <p className="text-sm text-gray-600 mb-4">{calc.description}</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-1">Logic:</p>
                        <p className="text-sm font-semibold bg-white p-3 rounded border border-gray-200 font-mono">
                          {calc.formula}
                        </p>
                      </div>
                      
                      {calc.subFormulas && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Implementation:</p>
                          <div className="space-y-2">
                            {calc.subFormulas.map((subFormula, idx) => (
                              <div key={idx} className="text-sm bg-white p-2 rounded border border-gray-100 font-mono">
                                {subFormula}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Compliance Calculations</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {complianceCalculations.map((calc) => (
                  <div
                    key={calc.id}
                    className={`p-5 rounded-lg border ${calc.borderColor} ${calc.bgColor}`}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`p-2 rounded-lg ${calc.textColor} bg-white`}>
                        {calc.icon}
                      </div>
                      <div>
                        <h4 className={`font-bold text-gray-800 mb-1 ${calc.textColor}`}>
                          {calc.title}
                        </h4>
                        <p className="text-sm text-gray-600 mb-4">{calc.description}</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-1">Calculation:</p>
                        <p className="text-sm font-semibold bg-white p-3 rounded border border-gray-200 font-mono">
                          {calc.formula}
                        </p>
                      </div>
                      
                      {calc.subFormulas && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Details:</p>
                          <div className="space-y-2">
                            {calc.subFormulas.map((subFormula, idx) => (
                              <div key={idx} className="text-sm bg-white p-2 rounded border border-gray-100 font-mono">
                                {subFormula}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-8 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
              <h4 className="font-semibold text-indigo-800 mb-2">Complete EUA Calculation</h4>
              <div className="text-sm text-indigo-700 space-y-2">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-white p-3 rounded border">
                    <div className="font-medium">Step 1: GHG Emissions</div>
                    <div className="text-xs font-mono mt-1">GHG = CO₂ + (CH₄×28) + (N₂O×265)</div>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <div className="font-medium">Step 2: ETS Coverage</div>
                    <div className="text-xs font-mono mt-1">GHG_ETS = GHG × MRV Fraction</div>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <div className="font-medium">Step 3: Reduction Factor</div>
                    <div className="text-xs font-mono mt-1">Base EUAs = GHG_ETS × Reduction</div>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <div className="font-medium">Step 4: Ice Discount</div>
                    <div className="text-xs font-mono mt-1">Final EUAs = Base EUAs × 0.95</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EUMRVCalculations;